import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';

export interface Message {
  role: 'user' | 'coach';
  content: string;
}

interface ChatService {
  startChat: (userId: number, categoryPrompt: string) => Promise<{
    sessionId: string;
    firstMessage: string;
  }>;
  continueChat: (request: {
    sessionId: string;
    userMessage: string;
    categoryPrompt: string;
  }) => Promise<{
    message: string;
    continue: boolean;
  }>;
}

interface UseChatProps {
  chatService: ChatService;
  categoryPrompt: string;
  fallbackMessage: string;
  errorMessage?: string;
  autoContinue?: boolean;
  onAutoContinue?: () => void;
}

interface UseChatReturn {
  messages: Message[];
  sessionId: string | null;
  showContinueButton: boolean;
  isLoading: boolean;
  isSending: boolean;
  sendMessage: (message: string) => Promise<void>;
  resetChat: () => void;
  autoContinueTriggered: boolean;
}

export const useChat = ({
  chatService,
  categoryPrompt,
  fallbackMessage,
  errorMessage = "I'm sorry, there was an error processing your message. Please try again.",
  autoContinue = false,
  onAutoContinue
}: UseChatProps): UseChatReturn => {
  const [messages, setMessages] = useState<Message[]>([]);
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [showContinueButton, setShowContinueButton] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isSending, setIsSending] = useState(false);
  const [autoContinueTriggered, setAutoContinueTriggered] = useState(false);
  const { userId } = useAuth();

  const initializeChat = useCallback(async () => {
    if (!userId) {
      console.error('No user ID available');
      setIsLoading(false);
      return;
    }

    try {
      setIsLoading(true);
      const response = await chatService.startChat(userId, categoryPrompt);
      setSessionId(response.sessionId);
      setMessages([{
        role: 'coach',
        content: response.firstMessage
      }]);
    } catch (error) {
      console.error('Failed to initialize chat:', error);
      // Fallback message if API fails
      setMessages([{
        role: 'coach',
        content: fallbackMessage
      }]);
    } finally {
      setIsLoading(false);
    }
  }, [userId, categoryPrompt, chatService, fallbackMessage]);

  const sendMessage = useCallback(async (message: string) => {
    if (!sessionId || isSending) return;

    // Add user message to chat
    setMessages(prev => [...prev, {
      role: 'user',
      content: message
    }]);

    setIsSending(true);

    try {
      const response = await chatService.continueChat({
        sessionId,
        userMessage: message,
        categoryPrompt
      });

      // Add coach response to chat
      setMessages(prev => [...prev, {
        role: 'coach',
        content: response.message
      }]);

      // Show continue button if chat is complete
      if (!response.continue) {
        setShowContinueButton(true);
      }
    } catch (error) {
      console.error('Failed to send message:', error);
      // Add error message to chat
      setMessages(prev => [...prev, {
        role: 'coach',
        content: errorMessage
      }]);
    } finally {
      setIsSending(false);
    }
  }, [sessionId, isSending, chatService, categoryPrompt, errorMessage]);

  const resetChat = useCallback(() => {
    setMessages([]);
    setSessionId(null);
    setShowContinueButton(false);
    setIsLoading(true);
    setIsSending(false);
    setAutoContinueTriggered(false);
    initializeChat();
  }, [initializeChat]);

  // Auto-trigger continue when session is ready
  useEffect(() => {
    if (showContinueButton && autoContinue && !autoContinueTriggered && onAutoContinue) {
      setAutoContinueTriggered(true);
      // Add a small delay to show the "session ready" message before auto-continuing
      setTimeout(() => {
        onAutoContinue();
      }, 1500);
    }
  }, [showContinueButton, autoContinue, autoContinueTriggered, onAutoContinue]);

  useEffect(() => {
    initializeChat();
  }, [initializeChat]);

  return {
    messages,
    sessionId,
    showContinueButton,
    isLoading,
    isSending,
    sendMessage,
    resetChat,
    autoContinueTriggered
  };
};